import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import billingAddonApi, { AddonImpactRow, BillingAddon } from '../../utils/api/billingAddon';
import divisionApi from '../../utils/api/division';

interface Props {
  addon: BillingAddon | null;
  onClose: () => void;
  onSaved: () => void;
  onDelete?: () => void;
  onImpactDetected?: (addon: BillingAddon, impact: AddonImpactRow[]) => void;
}

interface DivisionOption {
  id: number;
  name: string;
}

const BillingAddonModal: React.FC<Props> = ({ addon, onClose, onSaved, onDelete, onImpactDetected }) => {
  const isEdit = !!addon;

  const [name, setName] = useState(addon?.name ?? '');
  const [code, setCode] = useState(addon?.code ?? '');
  const [status, setStatus] = useState<'active' | 'inactive'>(addon?.status ?? 'active');
  const [selectedDivisionIds, setSelectedDivisionIds] = useState<number[]>(
    addon?.divisions?.map(d => d.id) ?? [],
  );
  const [allDivisions, setAllDivisions] = useState<DivisionOption[]>([]);
  const [divisionSearch, setDivisionSearch] = useState('');
  const [nameError, setNameError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    divisionApi.index().then((divs: any[]) => {
      setAllDivisions(divs.map(d => ({ id: d.id, name: d.name })));
    });
  }, []);

  const validateName = async () => {
    if (!name.trim()) { setNameError('Name is required'); return false; }
    const { unique } = await billingAddonApi.checkName(name.trim(), isEdit ? addon!.id : undefined);
    if (!unique) { setNameError('An add-on with this name already exists'); return false; }
    setNameError('');
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!code.trim()) { toast.error('Code is required'); return; }
    const valid = await validateName();
    if (!valid) return;

    if (isEdit && addon!.status === 'active' && status === 'inactive') {
      const impact = await billingAddonApi.impact(addon!.id);
      if (impact.length > 0) {
        onImpactDetected?.(addon!, impact);
        return;
      }
    }

    setSaving(true);
    try {
      if (isEdit) {
        await billingAddonApi.update(addon!.id, {
          name: name.trim(),
          code: code.trim(),
          status,
          divisionIds: selectedDivisionIds,
        });
        toast.success('Add-on updated');
      } else {
        await billingAddonApi.create({
          name: name.trim(),
          code: code.trim(),
          status,
          divisionIds: selectedDivisionIds,
        });
        toast.success('Add-on created');
      }
      onSaved();
    } catch {
      toast.error('Failed to save add-on');
    } finally {
      setSaving(false);
    }
  };

  const toggleDivision = (id: number) => {
    setSelectedDivisionIds(prev =>
      prev.includes(id) ? prev.filter(d => d !== id) : [...prev, id],
    );
  };

  return (
    <div style={overlayStyle}>
      <div style={dialogStyle}>
        <h3 style={{ marginTop: 0, marginBottom: 20 }}>
          {isEdit ? 'Edit Add-on' : 'New Billing Add-on'}
        </h3>
        <form onSubmit={handleSubmit}>
          <div style={fieldStyle}>
            <label style={labelStyle}>Name *</label>
            <input
              value={name}
              onChange={e => { setName(e.target.value); setNameError(''); }}
              onBlur={validateName}
              style={{ ...inputStyle, borderColor: nameError ? '#c0392b' : '#ccc' }}
              placeholder="Add-on name"
            />
            {nameError && <span style={{ color: '#c0392b', fontSize: 12 }}>{nameError}</span>}
          </div>

          <div style={fieldStyle}>
            <label style={labelStyle}>Code *</label>
            <input
              value={code}
              onChange={e => setCode(e.target.value)}
              style={inputStyle}
              placeholder="Billing code"
            />
          </div>

          <div style={fieldStyle}>
            <label style={labelStyle}>Status</label>
            <div style={{ display: 'flex', gap: 12 }}>
              {(['active', 'inactive'] as const).map(s => (
                <label key={s} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 13 }}>
                  <input
                    type="radio"
                    checked={status === s}
                    onChange={() => setStatus(s)}
                  />
                  {s.charAt(0).toUpperCase() + s.slice(1)}
                </label>
              ))}
            </div>
          </div>

          <div style={fieldStyle}>
            <label style={labelStyle}>Assign to Divisions</label>
            <input
              type="text"
              placeholder="Search divisions…"
              value={divisionSearch}
              onChange={e => setDivisionSearch(e.target.value)}
              style={{ ...inputStyle, marginBottom: 6 }}
            />
            <div style={{ maxHeight: 160, overflowY: 'auto', border: '1px solid #eee', borderRadius: 4, padding: '4px 8px' }}>
              {allDivisions.length === 0 ? (
                <p style={{ margin: 0, color: '#999', fontSize: 12 }}>Loading…</p>
              ) : (
                allDivisions.filter(d => d.name.toLowerCase().includes(divisionSearch.toLowerCase())).map(div => (
                  <div
                    key={div.id}
                    onClick={() => toggleDivision(div.id)}
                    style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0', cursor: 'pointer', userSelect: 'none' }}
                  >
                    <input
                      type="checkbox"
                      readOnly
                      checked={selectedDivisionIds.includes(div.id)}
                      style={{ width: 14, height: 14, margin: 0, flexShrink: 0, cursor: 'pointer' }}
                    />
                    <span style={{ fontSize: 13, lineHeight: 1.3 }}>{div.name}</span>
                  </div>
                ))
              )}
            </div>
          </div>

          <div style={{ display: 'flex', gap: 8, marginTop: 20 }}>
            {isEdit && onDelete && (
              <button type="button" onClick={onDelete} style={deleteBtnStyle}>Delete</button>
            )}
            <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
              <button type="button" onClick={onClose} style={cancelBtnStyle}>Cancel</button>
              <button type="submit" disabled={saving} style={saveBtnStyle}>
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

const overlayStyle: React.CSSProperties = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1001,
};
const dialogStyle: React.CSSProperties = {
  background: '#fff', borderRadius: 6, padding: 28, width: 480,
  maxWidth: '95vw', maxHeight: '90vh', overflowY: 'auto',
  boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
};
const fieldStyle: React.CSSProperties = { marginBottom: 16 };
const labelStyle: React.CSSProperties = { display: 'block', fontWeight: 600, fontSize: 13, marginBottom: 4 };
const inputStyle: React.CSSProperties = {
  width: '100%', boxSizing: 'border-box', padding: '6px 10px',
  border: '1px solid #ccc', borderRadius: 4, fontSize: 13,
};
const cancelBtnStyle: React.CSSProperties = {
  background: 'none', border: '1px solid #ccc', padding: '6px 16px',
  borderRadius: 4, cursor: 'pointer', fontSize: 13,
};
const deleteBtnStyle: React.CSSProperties = {
  background: '#c0392b', color: '#fff', border: 'none', padding: '6px 16px',
  borderRadius: 4, cursor: 'pointer', fontSize: 13, fontWeight: 600,
};
const saveBtnStyle: React.CSSProperties = {
  background: '#1D9E75', color: '#fff', border: 'none', padding: '6px 20px',
  borderRadius: 4, cursor: 'pointer', fontSize: 13, fontWeight: 600,
};

export default BillingAddonModal;
